import { getGlobalFlagOverrides } from './evals/context/global-flags';
import {
  getEvalContext,
  updateEvalContext,
  addOutOfScopeFlag,
  setConfigScope,
} from './evals/context/storage';
import { validateCliFlags } from './validate-flags';
import {
  parsePath,
  dotNotationToNested,
  isValidPath,
  getValueAtPath,
  buildSchemaForPath,
  findSchemaAtPath,
} from './util/dot-path';
import {
  getDef,
  getKind,
  getInnerType,
  getShape,
  isObjectSchema,
  assertZodV4,
} from './util/zod-internals';
import { trace } from '@opentelemetry/api';
import { type z, type ZodObject, type ZodDefault, type ZodSchema } from 'zod';
import { toOtelAttribute } from './otel/utils/to-otel-attribute';
import { Attr } from './otel';

type DefaultMaxDepth = 8;

// Helper to recursively check if a schema has defaults (including nested objects)
type HasDefaults<S> = S extends { _zod: { def: { defaultValue: unknown } } }
  ? true
  : S extends ZodObject<infer Shape>
    ? {
        [K in keyof Shape]: HasDefaults<Shape[K]>;
      } extends Record<keyof Shape, true>
      ? true
      : false
    : false;

// Helper type to extract the underlying ZodObject from ZodDefault wrapper
type UnwrapSchema<T> = T extends ZodDefault<infer U> ? U : T;

// Helper to check if ALL fields in a schema have defaults
type AllFieldsHaveDefaults<Schema> =
  // First check if the schema itself has an object-level default
  Schema extends { _zod: { def: { defaultValue: unknown } } }
    ? true
    : // Otherwise recursively check if all fields have defaults
      HasDefaults<UnwrapSchema<Schema>>;

interface AppScopeConfig<
  FlagSchema extends ZodObject<any> | undefined = undefined,
  FactSchema extends ZodObject<any> | undefined = undefined,
> {
  flagSchema: FlagSchema;
  factSchema?: FactSchema;
}

/**
 * Recursive type to extract all possible paths from an object type.
 * Uses stack-based depth limiting for better performance.
 *
 * @template T - The object type to extract paths from
 * @template Stack - Internal stack counter (do not set manually)
 * @template MaxDepth - Maximum recursion depth (default: 8 for good balance)
 */
type ObjectPaths<
  T,
  Stack extends unknown[] = [],
  MaxDepth extends number = DefaultMaxDepth,
> = Stack['length'] extends MaxDepth
  ? never
  : T extends object
    ? {
        [K in keyof T]-?: K extends string | number
          ? `${K}` | `${K}.${ObjectPaths<T[K], [1, ...Stack], MaxDepth>}`
          : never;
      }[keyof T]
    : never;

// Type to get value at a specific path in an object
type ObjectPathValue<T, P extends string> = P extends keyof T
  ? T[P]
  : P extends `${infer K}.${infer Rest}`
    ? K extends keyof T
      ? ObjectPathValue<T[K], Rest>
      : never
    : never;

/**
 * Generate deep nested paths from flag schema.
 *
 * @template T - ZodObject to extract paths from
 * @template MaxDepth - Maximum recursion depth (default: 8, override for deeper nesting)
 * @example
 * // Default 8-level depth
 * type Paths = DotPaths<MySchema>
 *
 * // Custom depth for deeper nesting (impacts performance)
 * type DeepPaths = DotPaths<MySchema, 12>
 */
export type DotPaths<T extends ZodObject<any>, MaxDepth extends number = DefaultMaxDepth> = {
  [NS in keyof T['shape']]:
    | (string & NS) // Include the namespace itself
    | {
        [P in ObjectPaths<
          z.output<UnwrapSchema<T['shape'][NS]>>,
          [],
          MaxDepth
        >]: `${string & NS}.${P}`;
      }[ObjectPaths<z.output<UnwrapSchema<T['shape'][NS]>>, [], MaxDepth>];
}[keyof T['shape']];

type PathValue<T extends ZodObject<any>, P extends string> = P extends `${infer NS}.${infer Rest}`
  ? NS extends keyof T['shape']
    ? ObjectPathValue<z.output<UnwrapSchema<T['shape'][NS]>>, Rest>
    : never
  : P extends keyof T['shape']
    ? z.output<UnwrapSchema<T['shape'][P]>>
    : never;

type DotNotationFlagFunction<FS extends ZodObject<any> | undefined> =
  FS extends ZodObject<any> ? <P extends DotPaths<FS>>(path: P) => PathValue<FS, P> : never;

type FactFunction<SC extends ZodObject<any> | undefined> =
  SC extends ZodObject<any>
    ? <P extends DotPaths<SC> & string>(name: P, value: PathValue<SC, P>) => void
    : never;

type OverrideFlagsFunction<FS extends ZodObject<any> | undefined> =
  FS extends ZodObject<any>
    ? (partial: { [K in DotPaths<FS>]?: PathValue<FS, K> }) => void
    : (partial: Record<string, any>) => void;

type WithFlagsFunction<FS extends ZodObject<any> | undefined> =
  FS extends ZodObject<any>
    ? <T>(overrides: { [K in DotPaths<FS>]?: PathValue<FS, K> }, fn: () => T) => T
    : <T>(overrides: Record<string, any>, fn: () => T) => T;

type PickFlagsFunction<FS extends ZodObject<any> | undefined> =
  FS extends ZodObject<any>
    ? {
        // Spread arguments: pickFlags2('foo', 'bar')
        <K extends ReadonlyArray<DotPaths<FS> & string>>(...paths: K): K;
        // Array argument: pickFlags2(['foo', 'bar'])
        <K extends ReadonlyArray<DotPaths<FS> & string>>(paths: K): K;
      }
    : never;

export interface AppScope<
  FS extends ZodObject<any> | undefined,
  SC extends ZodObject<any> | undefined,
> {
  flag: DotNotationFlagFunction<FS>;
  fact: FactFunction<SC>;
  overrideFlags: OverrideFlagsFunction<FS>;
  withFlags: WithFlagsFunction<FS>;
  pickFlags: PickFlagsFunction<FS>;
  getAllDefaultFlags: () => Record<string, any>;
}

/**
 * Check if a flag path is covered by the picked flags.
 * @param flagPath - The flag path to check (e.g., 'foo', 'foo.bar')
 * @param pickedFlags - Array of picked flag paths
 * @returns true if the flag is covered by picked flags
 */
export function isPickedFlag(flagPath: string, pickedFlags?: string[]): boolean {
  if (!pickedFlags) {
    // If no picked flags are provided, all flags are allowed
    return true;
  }

  if (pickedFlags.length === 0) {
    // If no flags are picked, all flags are allowed
    return true;
  }

  return pickedFlags.some((picked) => {
    // Exact match
    if (flagPath === picked) {
      return true;
    }
    // Nested match: flagPath starts with picked flag followed by a dot
    if (flagPath.startsWith(picked + '.')) {
      return true;
    }
    return false;
  });
}

// Helper to recursively validate that schemas don't contain union types
function assertNoUnions(schema: unknown, path = 'schema'): void {
  if (!schema) return;

  const kind = getKind(schema);
  if (!kind) return;

  // Unwrap transparent containers
  if (kind === 'default' || kind === 'optional' || kind === 'nullable') {
    const innerType = getInnerType(schema);
    return assertNoUnions(innerType, path);
  }

  // Hard-fail on unions
  if (kind === 'union' || kind === 'discriminatedunion') {
    throw new Error(`[AxiomAI] Union types are not supported in flag schemas (found at "${path}")`);
  }

  // Recurse into compound types
  if (kind === 'object') {
    const shape = getShape(schema);
    if (shape) {
      for (const [k, v] of Object.entries(shape)) {
        assertNoUnions(v, `${path}.${k}`);
      }
    }
  } else if (kind === 'array') {
    const innerType = getInnerType(schema);
    if (innerType) {
      assertNoUnions(innerType, `${path}[]`);
    }
  } else if (kind === 'record') {
    const def = getDef(schema);
    const valueType = def?.valueType;
    if (valueType) {
      assertNoUnions(valueType, `${path}{}`);
    }
  }
}

/**
 * Recursively verify that all leaf fields in the schema have defaults.
 * Throws with a detailed error message listing all paths missing defaults.
 * TODO: this should probably be in an adapter, not the core lib...
 */
function ensureAllDefaults(schema: unknown, path = ''): void {
  const missingDefaults: string[] = [];

  function checkDefaults(current: unknown, currentPath: string): void {
    if (!current) return;

    const kind = getKind(current);
    if (!kind) return;

    const def = getDef(current);
    const hasDefault = def?.defaultValue !== undefined;

    // Unwrap transparent containers and check their inner type
    if (kind === 'default') {
      // This has a default, we're done - no need to check inner type
      return;
    }

    if (kind === 'optional' || kind === 'nullable') {
      // Transparent wrappers - check inner type
      const innerType = getInnerType(current);
      return checkDefaults(innerType, currentPath);
    }

    // ZodRecord is not allowed
    if (kind === 'record') {
      throw new Error(
        `[AxiomAI] ZodRecord is not supported in flag schemas (found at "${currentPath || 'root'}")\n` +
          `All flag fields must have known keys and defaults. Consider using z.object() instead.`,
      );
    }

    // For objects: if there's an object-level default, we're good
    // Otherwise, recursively check all fields
    if (kind === 'object') {
      if (hasDefault) {
        // Object-level default covers all nested fields
        return;
      }

      const shape = getShape(current);
      if (shape) {
        for (const [k, v] of Object.entries(shape)) {
          const nextPath = currentPath ? `${currentPath}.${k}` : k;
          checkDefaults(v, nextPath);
        }
      }
      return;
    }

    // For arrays: arrays are leaf types (no per-index access)
    // Just check if the array schema itself has a default
    if (kind === 'array') {
      if (!hasDefault) {
        missingDefaults.push(currentPath || 'root');
      }
      return;
    }

    // For all other types (primitives, etc): must have a default
    if (!hasDefault) {
      missingDefaults.push(currentPath || 'root');
    }
  }

  checkDefaults(schema, path);

  if (missingDefaults.length > 0) {
    throw new Error(
      `[AxiomAI] All flag fields must have defaults. Missing defaults for:\n` +
        missingDefaults.map((p) => `  - ${p}`).join('\n') +
        `\n\nAdd .default(value) to these fields or to their parent objects.`,
    );
  }
}

/**
 * Create a new application-level evaluation scope.
 *
 * @param config.flagSchema A zod object describing the schema for flags **(required)**
 * @param config.factSchema A zod object describing the schema for facts (optional)
 *
 * @example
 * import { z } from 'zod';
 *
 * const { flag, fact, withFlags, pickFlags, overrideFlags } = createAppScope({
 *   flagSchema: z.object({
 *     ui: z.object({
 *       darkMode: z.boolean().default(false),
 *       theme:    z.object({
 *         primary: z.string().default('#00f'),
 *       }),
 *     }),
 *     api: z.object({ 
       endpoint: z.string().default('/api') 
     }),
 *   }),
 *   factSchema: z.object({
 *     userAction: z.string(),
 *     timing: z.number(),
 *   }),
 * });
 *
 * // Typed flag access
 * const dark = flag('ui.darkMode'); // inferred boolean
 * const theme = flag('ui.theme'); // entire object
 * const primary = flag('ui.theme.primary'); // '#00f'
 * const endpoint = flag('api.endpoint'); // uses schema default
 *
 * // Typed fact recording
 * fact('userAction', 'clicked_button');
 * fact('timing', 1250);
 *
 * // Temporarily override flags for a block of code
 * withFlags({ 'ui.darkMode': true }, () => {
 *   // code here, `ui.darkMode` will be true in this block and reset after
 * });
 *
 * // Override flags globally for the current evaluation run
 * overrideFlags({ 'api.endpoint': '/custom' });
 */
// Overload: Require all fields to have defaults (compile-time check)
export function createAppScope<
  FlagSchema extends ZodObject<any>,
  FactSchema extends ZodObject<any> | undefined = undefined,
>(
  config: AllFieldsHaveDefaults<FlagSchema> extends true
    ? AppScopeConfig<FlagSchema, FactSchema>
    : {
        flagSchema: FlagSchema;
        factSchema?: FactSchema;
        __error__: 'createAppScope: flagSchema must have .default() for all leaf fields';
      },
): AppScope<FlagSchema, FactSchema>;

// Implementation signature: Keep broad for internal use
export function createAppScope<
  FlagSchema extends ZodObject<any> | undefined = undefined,
  FactSchema extends ZodObject<any> | undefined = undefined,
>(config: AppScopeConfig<FlagSchema, FactSchema>): AppScope<FlagSchema, FactSchema> {
  // Store schemas for runtime validation
  const flagSchemaConfig = config?.flagSchema;
  const factSchemaConfig = config?.factSchema;

  // Reject Zod v3 schemas
  if (flagSchemaConfig) {
    assertZodV4(flagSchemaConfig, 'flagSchema');
  }
  if (factSchemaConfig) {
    assertZodV4(factSchemaConfig, 'factSchema');
  }

  // reject union types
  if (flagSchemaConfig) {
    assertNoUnions(flagSchemaConfig, 'flagSchema');
  }

  // Ensure all fields have defaults
  if (flagSchemaConfig) {
    ensureAllDefaults(flagSchemaConfig);
  }

  // CLI validation with dot notation support
  if (flagSchemaConfig) {
    validateCliFlags(flagSchemaConfig);
  }

  // Helper to check if a path represents a namespace access (no dots after first segment)
  function isNamespaceAccess(segments: string[]): boolean {
    if (!flagSchemaConfig || segments.length === 0) return false;

    // For root namespace (like 'ui'), check if it exists in the ZodObject shape
    if (segments.length === 1) {
      return flagSchemaConfig.shape ? segments[0] in flagSchemaConfig.shape : false;
    }

    // For nested paths (like 'app.ui.layout'), need to check if the path points to an object schema
    const schema = findSchemaAtPath(flagSchemaConfig, segments);
    return isObjectSchema(schema);
  }

  // Helper function to check if a schema has complete defaults at runtime
  // This mirrors the compile-time AllFieldsHaveDefaults<> type

  // Recursively build object with all defaults from a Zod schema
  function buildObjectWithDefaults(schema: unknown): unknown {
    if (!schema) return undefined;

    const kind = getKind(schema);
    if (!kind) return undefined;

    // `directDefault`: default for the entire object
    // If this is not present, we try to construct the defaults from child fields (recursively)
    const directDefault = extractDefault(schema);
    if (directDefault !== undefined) {
      return directDefault;
    }

    // We can only collect defaults from child fields if we're dealing with an object (for a scalar, there are no children)
    if (kind === 'object') {
      const shape = getShape(schema);
      if (shape) {
        const result: Record<string, unknown> = {};

        for (const [key, fieldSchema] of Object.entries(shape)) {
          const fieldValue = buildObjectWithDefaults(fieldSchema);
          result[key] = fieldValue;
        }

        return result;
      }
    }

    // No direct default, and it's not an object
    return undefined;
  }

  function extractDefault(schema: unknown): unknown {
    if (!schema) return undefined;

    // Unwrap transparent containers first, checking for defaults at each level
    let current: unknown = schema;

    for (let i = 0; i < 10; i++) {
      const def = getDef(current);
      if (!def) break;

      // Check for default value at current level
      if (def.defaultValue !== undefined) {
        return typeof def.defaultValue === 'function' ? def.defaultValue() : def.defaultValue;
      }

      // Unwrap one level if possible
      const inner = getInnerType(current);
      if (inner) {
        current = inner;
      } else {
        // No more wrapping, stop here
        break;
      }
    }

    return undefined;
  }

  function validateFinalFlagValue(
    dotPath: string,
    value: unknown,
  ): { ok: true; parsed: unknown } | { ok: false } {
    if (!flagSchemaConfig) return { ok: true, parsed: value };

    const segments = parsePath(dotPath);

    // 1. Fast-path: validate directly with field-level schema
    const fieldSchema = findSchemaAtPath(flagSchemaConfig, segments);
    if (fieldSchema) {
      const direct = (fieldSchema as ZodSchema<any>).safeParse(value);
      if (direct.success) return { ok: true, parsed: direct.data };
      // If we have a field schema but validation failed, this is a real error
      return { ok: false };
    }

    // 2. If we don't have a field schema, check if the path is even valid in our schema
    // Don't validate values for paths that don't exist in the schema - just pass them through
    const hasValidNamespace = flagSchemaConfig.shape && segments[0] in flagSchemaConfig.shape;
    if (!hasValidNamespace) {
      // Invalid namespace - pass through without validation (for backward compatibility with fallback values)
      return { ok: true, parsed: value };
    }

    // 3. For valid namespaces but invalid paths, try nested object validation
    const nested = dotNotationToNested({ [dotPath]: value });
    const nestedResult = flagSchemaConfig.strict().partial().safeParse(nested);
    if (nestedResult.success) {
      const parsed = getValueAtPath(nestedResult.data, segments) ?? value;
      return { ok: true, parsed };
    }

    // 4. If nested validation failed but the namespace is valid, allow it for backward compatibility
    return { ok: true, parsed: value };
  }

  function hasUndefinedLeaves(obj: unknown): boolean {
    if (obj === undefined) return true;
    if (obj === null || typeof obj !== 'object') return false;
    return Object.values(obj).some((v) =>
      typeof v === 'object' && v !== null ? hasUndefinedLeaves(v) : v === undefined,
    );
  }

  /**
   * Get flag value with dot notation path support and schema validation.
   * All flag fields must have .default() values in the schema.
   * Precedence: CLI overrides -> Context overrides -> Schema defaults → Error
   *
   * @param path - Dot notation path to the flag (e.g., 'ui.theme' or 'api.timeout')
   * @returns The flag value or undefined if path is invalid
   */
  function flag<P extends string>(path: P): unknown {
    const segments = parsePath(path);

    const ctx = getEvalContext();
    const globalOverrides = getGlobalFlagOverrides();

    if (!isPickedFlag(path, ctx.pickedFlags)) {
      addOutOfScopeFlag(path);
    }

    let finalValue: any;
    let source: 'cli' | 'ctx' | 'schema' | undefined;

    // Flag precedence order:
    // 1. CLI overrides (getGlobalFlagOverrides)
    // 2. Eval context overrides (getEvalContext().flags)
    // 3. Schema/object defaults
    // 4. undefined + console.error

    // 1. Check CLI overrides first (highest priority)
    if (path in globalOverrides) {
      finalValue = globalOverrides[path];
      source = 'cli';
    }
    // 2. Check context overrides (from withFlags() or overrideFlags)
    else if (path in ctx.flags) {
      finalValue = ctx.flags[path];
      source = 'ctx';
    }
    // 3. Resolve from schema
    else {
      if (!flagSchemaConfig) {
        console.error(`[AxiomAI] Invalid flag: "${path}"`);
        return undefined;
      }

      // Check valid namespace
      const hasValidNamespace = flagSchemaConfig.shape && segments[0] in flagSchemaConfig.shape;
      if (!hasValidNamespace) {
        console.error(`[AxiomAI] Invalid flag: "${path}"`);
        return undefined;
      }

      const schemaForPath = findSchemaAtPath(flagSchemaConfig, segments);

      // If schema path doesn't exist, try extracting from parent object-level defaults
      if (!schemaForPath) {
        const namespaceSchema = findSchemaAtPath(flagSchemaConfig, [segments[0]]);
        if (namespaceSchema) {
          const namespaceObject = buildObjectWithDefaults(namespaceSchema);
          if (namespaceObject && typeof namespaceObject === 'object') {
            finalValue = getValueAtPath(namespaceObject, segments.slice(1));
          }
        }

        if (finalValue === undefined) {
          console.error(`[AxiomAI] Invalid flag: "${path}"`);
          return undefined;
        }
      }
      // Check if this is a namespace access (returning whole objects)
      else if (isNamespaceAccess(segments)) {
        finalValue = buildObjectWithDefaults(schemaForPath);

        // If buildObjectWithDefaults fails or returns incomplete object, try extracting from parent defaults
        if (finalValue === undefined || hasUndefinedLeaves(finalValue)) {
          const nsSchema = findSchemaAtPath(flagSchemaConfig, [segments[0]]);
          if (nsSchema) {
            const nsObj = buildObjectWithDefaults(nsSchema);
            if (nsObj && typeof nsObj === 'object') {
              const extracted = getValueAtPath(nsObj, segments.slice(1));
              if (extracted !== undefined) {
                finalValue = extracted;
              }
            }
          }
        }

        if (finalValue === undefined) {
          console.error(`[AxiomAI] Invalid flag: "${path}"`);
          return undefined;
        }
      }
      // Leaf access: try field-level default first
      else {
        finalValue = extractDefault(schemaForPath);

        // If no field-level default, try extracting from parent object-level default
        if (finalValue === undefined) {
          const nsSchema = findSchemaAtPath(flagSchemaConfig, [segments[0]]);
          if (nsSchema) {
            const nsObj = buildObjectWithDefaults(nsSchema);
            if (nsObj && typeof nsObj === 'object') {
              finalValue = getValueAtPath(nsObj, segments.slice(1));
            }
          }

          if (finalValue === undefined) {
            console.error(`[AxiomAI] Invalid flag: "${path}"`);
            return undefined;
          }
        }
      }

      source = 'schema';
    }

    // Validate only overrides (schema values are pre-validated)
    if (source !== 'schema') {
      const validation = validateFinalFlagValue(path, finalValue);
      if (!validation.ok) {
        console.error(`[AxiomAI] Invalid flag: "${path}" - value does not match schema`);
      }
    }

    updateEvalContext({ [path]: finalValue });

    const span = trace.getActiveSpan();
    if (span?.isRecording()) {
      const attr = toOtelAttribute(finalValue);
      if (attr) {
        span.setAttribute(Attr.__EXPERIMENTAL_Flag(path), attr);
      }
    }

    return finalValue;
  }

  /**
   * Record a typed fact value for tracking and telemetry with dot notation support.
   * @param name - The fact name/key
   */
  function fact<N extends string>(name: N, value: unknown): void {
    let finalValue = value;

    // Validate with schema if provided (but only log errors for now to match tests)
    if (factSchemaConfig) {
      const segments = parsePath(name);

      let success = true;
      // Fast path check - validate path exists in schema before creating nested object
      if (!isValidPath(factSchemaConfig, segments)) {
        success = false;
      } else {
        try {
          // Build a schema specific to this path that makes sibling fields optional
          const pathSchema = buildSchemaForPath(factSchemaConfig, segments);

          // Convert dot notation to nested object for validation
          const nested = dotNotationToNested({ [name]: value });
          const result = pathSchema.safeParse(nested);

          if (!result.success) {
            success = false;
          } else {
            finalValue = getValueAtPath(result.data, segments) ?? value;
          }
        } catch (_error) {
          // buildSchemaForPath can throw if schema structure is invalid
          success = false;
        }
      }

      if (!success) {
        console.error(`[AxiomAI] Invalid fact: "${name}"`);
      }
    }

    updateEvalContext(undefined, { [name]: finalValue });

    const span = trace.getActiveSpan();
    if (span?.isRecording()) {
      const attr = toOtelAttribute(finalValue);
      if (attr) {
        span.setAttribute(Attr.__EXPERIMENTAL_Fact(name), attr);
      }
    }
  }

  /**
   * Override flag values for the current evaluation context with type safety.
   * @param partial - Typed flag overrides that must match the flag schema paths and types
   */
  function overrideFlags(partial: Record<string, any>): void {
    const ctx = getEvalContext();
    Object.assign(ctx.flags, partial);
  }

  /**
   * Execute code with temporary flag overrides, automatically restoring original values.
   * @param overrides - Typed flag overrides that must match the flag schema paths and types
   * @param fn - Function to execute with the overridden flags
   * @returns The return value of the executed function
   */
  function withFlags<T>(overrides: Record<string, any>, fn: () => T): T {
    const ctx = getEvalContext();
    const originalFlags = { ...ctx.flags };

    // Apply overrides
    Object.assign(ctx.flags, overrides);

    try {
      return fn();
    } finally {
      // Restore original flags by clearing and reassigning
      Object.keys(ctx.flags).forEach((key) => delete ctx.flags[key]);
      Object.assign(ctx.flags, originalFlags);
    }
  }

  const pickFlags = ((...args: any[]) => {
    // Handle both array and spread arguments
    return args[0] && Array.isArray(args[0]) ? args[0] : args;
  }) as PickFlagsFunction<FlagSchema>;

  function flattenToDot(obj: any, prefix: string[] = [], out: Record<string, any> = {}) {
    if (obj && typeof obj === 'object' && !Array.isArray(obj)) {
      for (const [k, v] of Object.entries(obj)) {
        flattenToDot(v, [...prefix, k], out);
      }
    } else {
      if (prefix.length > 0) {
        out[prefix.join('.')] = obj;
      }
    }
    return out;
  }

  function getAllDefaultFlags(): Record<string, any> {
    if (!flagSchemaConfig) return {};
    const defaultsObj = buildObjectWithDefaults(flagSchemaConfig);
    if (defaultsObj && typeof defaultsObj === 'object') {
      return flattenToDot(defaultsObj as Record<string, any>);
    }
    return {};
  }

  const scope = {
    flag: flag as any as DotNotationFlagFunction<FlagSchema>,
    fact: fact as any as FactFunction<FactSchema>,
    overrideFlags: overrideFlags as any as OverrideFlagsFunction<FlagSchema>,
    withFlags: withFlags as any as WithFlagsFunction<FlagSchema>,
    pickFlags,
    getAllDefaultFlags,
  } as AppScope<FlagSchema, FactSchema>;

  // Expose scope to current eval context for downstream collection (suite-end summaries)
  setConfigScope(scope as any);

  return scope;
}
